import { useEffect, useRef, useState } from 'react';
import { TOOLTIP_DELAY_MS } from './Tooltip.types';

interface UseTooltipVisibility {
  isVisible: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleFocusCapture: () => void;
  handleBlurCapture: () => void;
}

export const useTooltipVisibility = (delay: boolean, hideOnFocus: boolean): UseTooltipVisibility => {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleMouseEnter = (): void => {
    if (hideOnFocus && isFocusedRef.current) return;

    if (delay) {
      timeoutRef.current = setTimeout(() => setIsVisible(true), TOOLTIP_DELAY_MS);
    } else {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = (): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  };

  const handleFocusCapture = (): void => {
    isFocusedRef.current = true;
    handleMouseLeave();
  };

  const handleBlurCapture = (): void => {
    isFocusedRef.current = false;
  };

  return { isVisible, setIsVisible, handleMouseEnter, handleMouseLeave, handleFocusCapture, handleBlurCapture };
};
